import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  AlertDialogCloseButton,
  RadioGroup,
  HStack,
  Radio,
  Input,
  Text,
  useToast,
} from '@chakra-ui/react'
import { Prescription } from '../../../types'
import { get } from 'lodash'
import { useReIssuePrescriptionMutation } from '../../../services/prescriptions'

interface CeasePrescriptionModelProps {
  onClose: () => void
  isOpen: boolean
  prescriptions: Prescription[]
}

export const ReIssuePrescriptionModal: FunctionComponent<CeasePrescriptionModelProps> = ({
  onClose,
  isOpen,
  prescriptions,
}) => {
  const [channel, setChannel] = React.useState<'email' | 'phone'>('phone')
  const [electronicAddress, setElectronicAddress] = useState<string>('')
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const toast = useToast()
  const [reIssuePrescription, reIssuePrescriptionResult] = useReIssuePrescriptionMutation()

  const onReIssuePrescription = async () => {
    const responses = await Promise.all(
      prescriptions.map(async (prescription) => {
        return await reIssuePrescription(
          channel === 'email'
            ? {
                scid: prescription.scid,
                email: electronicAddress,
              }
            : {
                scid: prescription.scid,
                phone: electronicAddress,
              }
        )
      })
    )

    const isAllSuccessful = responses.every(
      (response) => 'data' in response && response.data?.status === 'Success'
    )
    if (isAllSuccessful) {
      toast({
        title: 'Prescriptions re-sent successfully',
        description: 'The prescriptions have been re-sent successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
      onClose()
    } else {
      toast({
        title: 'Failed to re-send prescriptions',
        description: 'Some prescriptions failed to be re-sent. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
    }
  }

  useEffect(() => {
    if (!prescriptions.length) return
    if (channel === 'email') {
      setElectronicAddress(get(prescriptions[0], 'issueSnapshot.patient.email', ''))
    } else if (channel === 'phone') {
      setElectronicAddress(get(prescriptions[0], 'issueSnapshot.patient.phoneNumber', ''))
    }
  }, [channel, prescriptions])

  return (
    <>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay />

        <AlertDialogContent>
          <AlertDialogHeader>Re-send Prescriptions?</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <RadioGroup
              defaultValue="email"
              value={channel}
              onChange={(value: 'email' | 'phone') => setChannel(value)}
            >
              <HStack spacing="6">
                <Radio value="phone">Send via SMS</Radio>
                <Radio value="email">Send via email</Radio>
              </HStack>
            </RadioGroup>
            <Input
              mt={4}
              placeholder={channel}
              value={electronicAddress}
              onChange={(e) => setElectronicAddress(e.target.value)}
            />
            <Text mt={4}>
              Please note, changing these details will not update your patient details
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              isLoading={reIssuePrescriptionResult.isLoading}
              loadingText="Re-sending"
              colorScheme="blue"
              ml={3}
              onClick={onReIssuePrescription}
            >
              Send
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
